/**
 * Use stacked large article row when there is a need to promote 2-4 stories or promotions in one row when you want image stacked on top of content, and you also want to utilize the full width of the grid.
 *
 * @module views/components/ArticleRowStackedLarge
 * @memberof -Common
 */
import './ArticleRowStackedLarge.scss';

import React, { useRef } from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useIntersectionObserver } from '@ulta/core/hooks/useIntersectionObserver/useIntersectionObserver';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleIntersection } from '@ulta/core/utils/intersectionProcessor/intersectionProcessor';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import carouselUtils from '@ulta/utils/carouselUtils/carouselUtils';
import { constants } from '@ulta/utils/constants/constants';

import Article from '../Article/Article';
import { upCount } from '../ArticleRowStackedCompact/ArticleRowStackedCompact';

/**
 * Represents a ArticleRowStackedLarge component
 *
 * @method
 * @param {ArticleRowStackedLargeProps} props - React properties passed from composition
 * @returns ArticleRowStackedLarge
 */
export const ArticleRowStackedLarge = function( props ){
  const articleRowStackedLargeRef = useRef( null );

  const {
    articles,
    articleTextAlign,
    backgroundColor,
    carousel,
    dataCapture,
    inset,
    patternBackground,

    /* Rail Title */
    eyebrow,
    subtitle,
    title,

    /* Rail meta data */
    carouselAccessibility = defaultProps.carouselAccessibility,
    carouselCardAccessibility,
    carouselDetailsAccessibility,

    /* Intersection Observer */
    root = defaultProps.root,
    rootMargin = defaultProps.rootMargin,
    threshold = defaultProps.threshold
  } = props ;

  /* Set variables */
  const upCountText = upCount( articles?.length );
  const { breakpoint } = useDeviceInflection();
  const isMobile = breakpoint && ( breakpoint.CURRENT_BREAKPOINT === 'SM' );
  const carouselMobile = isMobile && props.carousel && props.articles?.length > 2;

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  useIntersectionObserver(
    articleRowStackedLargeRef, {
      root: root,
      rootMargin: rootMargin,
      threshold: threshold
    }, handleIntersection( props )
  );

  /* Interpolating accesibility strings */
  const newCarouselAccessibility = carouselUtils.handleTokenizedString( carouselAccessibility, [title] );
  const newCarouselCardAccessibility = carouselUtils.handleTokenizedString( carouselCardAccessibility, [title] );
  const newCarouselDetailsAccessibility = carouselUtils.handleTokenizedString( carouselDetailsAccessibility, [title] );

  /* ADA Objects */
  const _railADAmeta = {
    carouselAccessibility: newCarouselAccessibility,
    carouselCardAccessibility: newCarouselCardAccessibility,
    carouselDetailsAccessibility: newCarouselDetailsAccessibility
  };

  if( !articles || !articleTextAlign ){
    return null;
  }

  return (
    <div className={
      classNames( 'ArticleRowStackedLarge', {
        'ArticleRowStackedLarge__withBackgroundColor': backgroundColor && !patternBackground,
        [`ArticleRowStackedLarge__numArticle--0${articles?.length}`]:articles
      } ) }
    ref={ articleRowStackedLargeRef }
    >
      <div className={
        classNames( 'ArticleRowStackedLarge__layout', {
          ['ArticleRowStackedLarge__layout--inset']:inset,
          ['ArticleRowStackedLarge__layout--standard']:!carousel
        } )
      }
      >
        { patternBackground &&
          <div className='ArticleRowStackedLarge__patternBackground'>
            <UltaAsset
              { ...spreadProps( { ...patternBackground } ) }
            />
          </div>
        }
        <GridContainer
          { ...spreadProps( {
            fullBleedContentBackgroundColor: !patternBackground ? backgroundColor : undefined
          } ) }
        >
          <div className={
            classNames( 'ArticleRowStackedLarge__mainContainer', {
              ['ArticleRowStackedLarge__mainContainer--carousel']:carousel,
              ['ArticleRowStackedLarge__mainContainer--standard']:!carousel
            } )
          }
          >
            { eyebrow &&
              <div className={ classNames( 'ArticleRowStackedLarge__headingEyebrow', {
                'ArticleRowStackedLarge__headingEyebrow--withPatternBgPosition': patternBackground
              } )
              }
              >
                <Text
                  htmlTag='p'
                  textStyle='eyebrow'
                  textAlign='center'
                >
                  { eyebrow }
                </Text>
              </div>
            }
            { title?.text &&
              <div className={ classNames( 'ArticleRowStackedLarge__headingTitle', {
                'ArticleRowStackedLarge__headingTitle--withPatternBgPosition': patternBackground && !eyebrow
              } )
              }
              >
                <Text
                  htmlTag={ title.htmlTag }
                  textStyle='title-4'
                  textAlign='center'
                >
                  { title.text }
                </Text>
              </div>
            }
            { subtitle &&
              <div className='ArticleRowStackedLarge__headingSubtitle'>
                <Text
                  htmlTag='p'
                  textStyle='subtitle-1'
                  textAlign='center'
                >
                  { subtitle }
                </Text>
              </div>
            }
            <div className='ArticleRowStackedLarge__articleContainer'>
              { articles && carouselMobile &&
                <div className='ArticleRowStackedLarge__carousel'>
                  <Rail
                    railRef={ articleRowStackedLargeRef }
                    id={ railUuid }
                    items={ articles }
                    dataCapture={ dataCapture }
                    _railADAmeta={ _railADAmeta }
                    sliderPerViewSM={ 1.6 }
                    slidesPerGroupSM={ 2 }
                  >
                    { articles?.length > 0 && articles.map( ( content, index ) => (
                      <Slide key={ index }>
                        <div
                          aria-label={ `Article ${index + 1} of ${articles.length}` }
                        >
                          <Article
                            { ...spreadProps( { ...content } ) }
                            cardOrientation='vertical'
                            articleTextAlign={ articleTextAlign }
                            backgroundColor={ backgroundColor }
                            upCountText={ upCountText }
                            upCountCounter={ index }
                            key={ index }
                          />
                        </div>
                      </Slide>
                    ) )
                    }
                  </Rail>
                </div>
              }
              { articles && !carouselMobile &&
                articles.map( ( content, index ) => (
                  <div className='ArticleRowStackedLarge__contentContainer'
                    key={ index }
                  >
                    <Article
                      { ...spreadProps( { ...content } ) }
                      cardOrientation='vertical'
                      articleTextAlign={ articleTextAlign }
                      backgroundColor={ backgroundColor }
                      upCountText={ upCountText }
                      upCountCounter={ index }
                    />
                  </div>
                ) )
              }
            </div>
          </div>
        </GridContainer>
      </div>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef ArticleRowStackedLargeProps
 * @type {object}
 * @property {object} title - Sets the title properties
 * @property {string} eyebrow - Sets the eyebrow
 * @property {string} subtitle - Sets the subtitle
 * @property {cms-checkbox} carousel - Boolean value for carousel
 * @property {string} backgroundColor - Sets the backgroundColor
 * @property {string} patternBackground - Sets the patternBackground
 * @property {object} articles - Sets the article properties
 * @property {string} articleTextAlign - Sets the article text alignment
 * @property {object} linkAction - Sets the linkAction
 */

export const propTypes = {
  title: PropTypes.object,
  eyebrow: PropTypes.string,
  subtitle: PropTypes.string,
  carousel: PropTypes.bool,
  inset: PropTypes.bool,
  backgroundColor: PropTypes.string,
  patternBackground:  PropTypes.object,
  articles: PropTypes.array,
  articleTextAlign: PropTypes.string,
  linkAction: PropTypes.object
};

/**
 * Default values for passed properties
 * @type {object}
 * @property {string} carouselDetailsAccessibility='This is a carousel of items with next and previous button to move through the slides' - The default value for carousel is aria-label
 * @property {string} carouselAccessibility='carousel' - The default value for aria-label is set to text 'carousel'.
 */

export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS,
  carouselAccessibility :'carousel of articles'
};

ArticleRowStackedLarge.propTypes = propTypes;
ArticleRowStackedLarge.defaultProps = defaultProps;

export default ArticleRowStackedLarge;